import React, { useState, useEffect, useContext } from "react";
import {
  LoadingOverlay,
  Box,
  Container,
  NativeSelect,
  Alert,
  Select,
  Text,
  TextInput,
  Button,
  Group,
  Stack,
  Center,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { DataContext } from "../../../context/DataContext";
import {
  IconShieldCheck,
  IconShieldCancel,
  IconPhoneCheck,
} from "@tabler/icons-react";
import useStatusCheck from "../../../hooks/useStatusCheck";
import DataCollectionForm from "../../../components/data-collection-form";
import useLocale from "../../../hooks/useLocale";
import {
  getAddressRegion,
  isDayFirstRegion,
  ADDRESS_REGION_UK,
  ADDRESS_REGION_DE,
} from "../../../utils/addressRegion";

export default function AgeMatch() {
  const { text, data, settings } = useContext(DataContext);
  // Journey-level `text.general.*` overrides win; the agematch locale set
  // (44 languages, English fallback) fills in everything the journey
  // doesn't set.
  const locale = useLocale("agematch");
  const currentPath = "agematch/process";
  const [formConfig, setFormConfig] = useState(null);
  const [reattempts, setReattempts] = useState(1);

  useEffect(() => {
    const initialFields = [
      {
        name: "fn",
        label: text?.general?.firstName || locale.form.firstName,
        initialValue: data?.fn || "",
        group: "name",
        autoComplete: "given-name",
        enterKeyHint: "next",
        validation: (value) =>
          value.length < 2
            ? text?.general?.firstNameError || locale.form.firstNameError
            : null,
      },
      {
        name: "ln",
        label: text?.general?.lastName || locale.form.lastName,
        initialValue: data?.ln || "",
        group: "name",
        autoComplete: "family-name",
        enterKeyHint: "next",
        validation: (value) =>
          value.length < 2
            ? text?.general?.lastNameError || locale.form.lastNameError
            : null,
      },
    ];

    let additionalFields = [];

    const matchedRoute = settings?.journey?.route?.find(
      (route) => route.path === currentPath,
    );

    if (matchedRoute) {
      setReattempts(matchedRoute.reattempts || 1);
    }

    const lower = (list) =>
      (Array.isArray(list) ? list : []).map((input) =>
        typeof input === "string" ? input.toLowerCase() : input,
      );
    // `inputs` must be filled in; `optional_inputs` (admin: field set to
    // Optional) are shown too but may be left blank. get-session never puts
    // a name in both.
    const inputs = lower(matchedRoute?.inputs);
    const optionalInputs = lower(matchedRoute?.optional_inputs);
    const asks = (name) => inputs.includes(name) || optionalInputs.includes(name);
    const isOptional = (name) => optionalInputs.includes(name);
    // Blank is fine for an optional field; anything typed is still checked.
    const optionalOr = (name, validate) => (value) =>
      isOptional(name) && (value === undefined || value === null || String(value).trim() === "")
        ? null
        : validate(value);
    const addressRegion = getAddressRegion(settings, currentPath);
    const isUKAddressSetting = addressRegion === ADDRESS_REGION_UK;
    const isDeAddressSetting = addressRegion === ADDRESS_REGION_DE;
    const needsAddress =
      isUKAddressSetting || isDeAddressSetting || inputs.includes("address");
    const needsDob = asks("dob");
    // Extra inputs the route step turned on in the admin (AgeMatch
    // "Also ask for": phone, SSN). They feed the AgeMatch rule set the
    // verification consumer picks from the step's checks.
    const needsPhone = asks("phone");
    const needsSsn = asks("ssn") || asks("ssn9");
    const wantsFullSsn = asks("ssn9");
    const ssnOptional = isOptional("ssn") || isOptional("ssn9");

    if (needsAddress) {
      additionalFields.push({
        name: "address",
        label: text?.general?.address || locale.address.label,
        type: "address",
        // Address has its own dedicated step in AgeMatch, so stack the
        // City / State / Zip fields vertically to avoid squishing the Zip.
        stacked: true,
        enterKeyHint: "next",
        validation: (value) => {
          let addrError = locale.form.addressRequired;

          if (isDeAddressSetting) {
            // German: street + house number, 5-digit PLZ, city. Bundesland
            // is optional and travels in `state` ("" when left blank) so
            // the payload keeps the addr/city/state/zip shape the
            // verification consumer maps onto AgeMatch.
            if (
              value.addr &&
              value.city &&
              /^\d{5}$/.test((value.zip || "").trim())
            ) {
              addrError = null;
            }
          } else if (isUKAddressSetting) {
            const ukPostcodePattern =
              /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i;
            if (
              value.addr &&
              value.city &&
              value.state &&
              ukPostcodePattern.test(value.zip.trim())
            ) {
              addrError = null;
            }
          } else if (
            value.addr &&
            value.city &&
            value.state &&
            value.zip.trim().length === 5
          ) {
            // The address is valid for US format.
            addrError = null;
          }

          return addrError;
        },
      });
    }

    if (needsDob) {
      additionalFields.push({
        name: "dob",
        label: isOptional("dob")
          ? `${text?.general?.DateOfBirth || locale.form.dob} (${text?.general?.optional || "optional"})`
          : text?.general?.DateOfBirth || locale.form.dob,
        type: "date",
        // UK and DE users type their date day-first; the wire format stays
        // YYYYMMDD (DateOfBirthSelect reorders before onDobChange).
        dayFirst: isDayFirstRegion(addressRegion),
        placeholder: isDayFirstRegion(addressRegion)
          ? locale.form.dobPlaceholderDayFirst
          : locale.form.dobPlaceholderMonthFirst,
        optional: isOptional("dob"),
        validation: optionalOr("dob", (value) => {
          const dobPattern =
            /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/;
          const isDobFormatCorrect = dobPattern.test(value);

          let dobError =
            text?.general?.DateOfBirthError || locale.form.dobError;

          if (isDobFormatCorrect) {
            const year = parseInt(value.substring(0, 4), 10);
            const month = parseInt(value.substring(4, 6), 10) - 1; // JS months are 0-indexed
            const day = parseInt(value.substring(6, 8), 10);

            const date = new Date(year, month, day);

            if (
              date.getFullYear() === year &&
              date.getMonth() === month &&
              date.getDate() === day
            ) {
              dobError = null;
            }
          }
          return dobError;
        }),
      });
    }

    if (needsPhone) {
      additionalFields.push({
        name: "phone",
        label: isOptional("phone")
          ? `${text?.general?.phone || "Phone Number"} (${text?.general?.optional || "optional"})`
          : text?.general?.phone || "Phone Number",
        type: "tel",
        enterKeyHint: "next",
        optional: isOptional("phone"),
        validation: optionalOr("phone", (value) => {
          const digitsOnly = (value || "").replace(/\D/g, "");
          return digitsOnly.length !== 10
            ? text?.general?.phoneError || "Phone number must be 10 digits"
            : null;
        }),
      });
    }

    if (needsSsn) {
      const ssnLabel = wantsFullSsn
        ? text?.general?.ssnFull || "Social Security Number"
        : text?.general?.ssnLast4 || "SSN (last 4)";
      additionalFields.push({
        name: "ssn",
        label: ssnOptional ? `${ssnLabel} (${text?.general?.optional || "optional"})` : ssnLabel,
        type: "tel",
        enterKeyHint: "next",
        optional: ssnOptional,
        validation: (value) => {
          const digitsOnly = (value || "").replace(/\D/g, "");
          if (ssnOptional && digitsOnly.length === 0) return null;
          if (wantsFullSsn) {
            return digitsOnly.length === 9
              ? null
              : text?.general?.ssnError || "Enter your 9-digit SSN";
          }
          return digitsOnly.length === 4 || digitsOnly.length === 9
            ? null
            : text?.general?.ssnError || "Enter the last 4 digits of your SSN";
        },
      });
    }

    setFormConfig({ fields: [...initialFields, ...additionalFields] });
  }, [data, currentPath, text, settings, locale]);

  if (!formConfig) {
    return <LoadingOverlay visible />;
  }

  // Route step setting (admin: AgeMatch → "Submit automatically when
  // prefilled", off by default). get-session puts the step's resolved
  // settings on the matching route entry.
  const autoSubmitPrefilled =
    settings?.journey?.route?.find((route) => route.path === currentPath)
      ?.settings?.auto_submit_prefilled === true;

  return (
    <DataCollectionForm
      config={formConfig}
      title={locale.header.title}
      subtitle={locale.header.subtitle}
      buttonText={locale.button.verify}
      icon={
        <IconShieldCheck
          size={40}
          stroke={1.5}
          //color={data.styles?.primaryColor ? data.styles.primaryColor : "teal"}
        />
      }
      componentKey={currentPath}
      endpoint="/v3/stable/api/frontend/agematch"
      reAttempts={reattempts}
      skipInitialAutoSubmit={!autoSubmitPrefilled}
    />
  );
}
